import type {
  EquipmentBusinessDayAdjustment,
  EquipmentCalendarPattern,
  EquipmentCycleBaselineCapture,
  EquipmentCycleCalendarAmount,
  EquipmentCycleElapsedAmount,
  EquipmentCycleEventReference,
  EquipmentCycleExpiryRule,
  EquipmentCyclePolicy,
  EquipmentCycleStartRule,
  EquipmentCycleTimeExpression,
  EquipmentDstGapPolicy,
  EquipmentDstOverlapPolicy,
  EquipmentElapsedTemporalExpression,
  EquipmentMissedCyclePolicy,
  EquipmentMonthEndPolicy,
  EquipmentPolicyOverride,
  EquipmentPolicyOverrideMode,
  EquipmentStateAction,
  EquipmentStateActionOrigin,
  EquipmentStateApplicableResourceKind,
  EquipmentStateAttribute,
  EquipmentStateAttributeKind,
  EquipmentStateBinding,
  EquipmentStateCatalogMutationResult,
  EquipmentStateCatalogRebaseResult,
  EquipmentStateCatalogVersion,
  EquipmentStateCatalogVersionStatus,
  EquipmentStateCatalogWorkspace,
  EquipmentStateClass,
  EquipmentStateCondition,
  EquipmentStateEffect,
  EquipmentStateEffectType,
  EquipmentStateEffectTiming,
  EquipmentStateFabricationType,
  EquipmentStateGuardOperator,
  EquipmentStateIssueHardness,
  EquipmentStateIssueSeverity,
  EquipmentStateSemanticState,
  EquipmentStateTimerBasis,
  EquipmentStateTemporalEventReference,
  EquipmentStateTemporalTransition,
  EquipmentStateTimeDrivenTransition,
  EquipmentStateTopologyBaseline,
  EquipmentStateTopologyResource,
  EquipmentStateValidationIssue,
  EquipmentStateValue,
  EquipmentTemporalExpression,
  EquipmentWeekday,
} from '../types/prodEquipmentStateCatalog';

type RawRecord = Record<string, unknown>;

const asRecord = (value: unknown): RawRecord => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as RawRecord : {}
);

const pick = (raw: RawRecord, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (raw[key] !== undefined) return raw[key];
  }
  return undefined;
};

const asString = (value: unknown, fallback = ''): string => (
  typeof value === 'string' ? value : value == null ? fallback : String(value)
);

const asNullableString = (value: unknown): string | null => (
  value == null || value === '' ? null : String(value)
);

const asNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asNullableNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const asBoolean = (value: unknown): boolean => (
  value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true'
);

const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

const enumValue = <T extends string>(value: unknown, values: readonly T[], fallback: T): T => {
  const normalized = asString(value).toUpperCase() as T;
  return values.includes(normalized) ? normalized : fallback;
};

const VERSION_STATUSES: EquipmentStateCatalogVersionStatus[] = [
  'DRAFT', 'VALIDATED', 'PUBLISHED', 'SUPERSEDED', 'ABANDONED',
];
const RESOURCE_KINDS: EquipmentStateApplicableResourceKind[] = [
  'ROOM', 'EQUIPMENT', 'COMPONENT', 'CIP_STATION',
];
const ATTRIBUTE_KINDS: EquipmentStateAttributeKind[] = ['DISCRETE', 'COUNTER', 'CLOCK'];
const FABRICATION_TYPES: EquipmentStateFabricationType[] = ['SS', 'SUS', 'FIXED', 'CONSUMABLE', 'OTHER'];
const ACTION_ORIGINS: EquipmentStateActionOrigin[] = ['PRIMARY', 'DERIVABLE'];
const OPERATORS: EquipmentStateGuardOperator[] = [
  'EQ', 'NE', 'LT', 'LTE', 'GT', 'GTE', 'IN', 'NOT_IN',
  'IS_SET', 'IS_NOT_SET', 'NOT_EXPIRED', 'EXPIRED',
];
const EFFECT_TYPES: EquipmentStateEffectType[] = [
  'SET_STATE', 'ACTIVATE_TEMPORAL_STATE', 'INCREMENT', 'RESET_COUNTER', 'START_TIMER', 'CLEAR_TIMER',
];
const EFFECT_TIMINGS: EquipmentStateEffectTiming[] = ['START', 'END'];
const TIMER_BASES: EquipmentStateTimerBasis[] = ['DHT', 'CHT', 'OTHER'];
const SEMANTIC_STATES: EquipmentStateSemanticState[] = [
  'idle', 'processing', 'cleaning_cip', 'sterilizing_sip',
  'dirty_hold', 'clean_hold', 'changeover', 'maintenance',
];
const MONTH_END_POLICIES: EquipmentMonthEndPolicy[] = ['CLAMP', 'PRESERVE_END_OF_MONTH', 'ROLL_FORWARD', 'SKIP'];
const DST_GAP_POLICIES: EquipmentDstGapPolicy[] = ['SHIFT_FORWARD', 'SKIP', 'ERROR'];
const DST_OVERLAP_POLICIES: EquipmentDstOverlapPolicy[] = ['EARLIER_OFFSET', 'LATER_OFFSET'];
const BUSINESS_ADJUSTMENTS: EquipmentBusinessDayAdjustment[] = [
  'NONE', 'FOLLOWING', 'PRECEDING', 'MODIFIED_FOLLOWING', 'MODIFIED_PRECEDING', 'SKIP',
];
const MISSED_CYCLE_POLICIES: EquipmentMissedCyclePolicy[] = ['CATCH_UP_ALL', 'CATCH_UP_LATEST', 'SKIP'];
const OVERRIDE_MODES: EquipmentPolicyOverrideMode[] = ['INHERIT', 'DISABLED', 'REPLACE'];
const WEEKDAYS: EquipmentWeekday[] = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY',
];

const elapsedExpression = (value = 1, unit: EquipmentElapsedTemporalExpression['unit'] = 'DAY'): EquipmentElapsedTemporalExpression => ({
  basis: 'ELAPSED_DURATION',
  value,
  unit,
  monthEndPolicy: null,
  timeZone: null,
  dstGapPolicy: null,
  dstOverlapPolicy: null,
});

export const mapEquipmentTemporalExpression = (
  value: unknown,
  legacyDurationMinutes?: unknown,
): EquipmentTemporalExpression | null => {
  const raw = asRecord(value);
  if (Object.keys(raw).length === 0) {
    const legacy = asNullableNumber(legacyDurationMinutes);
    return legacy && legacy > 0 ? elapsedExpression(legacy, 'MINUTE') : null;
  }
  const basis = asString(pick(raw, 'basis')).toUpperCase();
  const amount = asNumber(pick(raw, 'value'), 0);
  if (basis === 'CALENDAR_PERIOD') {
    const unit = enumValue(
      pick(raw, 'unit'),
      ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const,
      'DAY',
    );
    const rawMonthEnd = pick(raw, 'monthEndPolicy', 'month_end_policy');
    return {
      basis: 'CALENDAR_PERIOD',
      value: amount,
      unit,
      monthEndPolicy: rawMonthEnd == null || rawMonthEnd === ''
        ? null
        : enumValue(rawMonthEnd, MONTH_END_POLICIES, 'CLAMP'),
      timeZone: asString(pick(raw, 'timeZone', 'time_zone'), 'Asia/Shanghai'),
      dstGapPolicy: enumValue(pick(raw, 'dstGapPolicy', 'dst_gap_policy'), DST_GAP_POLICIES, 'SHIFT_FORWARD'),
      dstOverlapPolicy: enumValue(
        pick(raw, 'dstOverlapPolicy', 'dst_overlap_policy'),
        DST_OVERLAP_POLICIES,
        'EARLIER_OFFSET',
      ),
    };
  }
  return elapsedExpression(
    amount,
    enumValue(pick(raw, 'unit'), ['SECOND', 'MINUTE', 'HOUR', 'DAY'] as const, 'MINUTE'),
  );
};

export const unwrapEquipmentStateCatalogData = (payload: unknown): unknown => {
  const raw = asRecord(payload);
  return raw.data ?? payload;
};

export const mapEquipmentStateCatalogVersion = (value: unknown): EquipmentStateCatalogVersion => {
  const raw = asRecord(value);
  return {
    id: asString(pick(raw, 'id', 'versionId', 'version_id')),
    facilityCode: asString(pick(raw, 'facilityCode', 'facility_code'), 'F1'),
    versionNo: asNumber(pick(raw, 'versionNo', 'version_no', 'versionNumber', 'version_number')),
    name: asString(pick(raw, 'name', 'versionName', 'version_name'), '未命名版本'),
    status: enumValue(pick(raw, 'status'), VERSION_STATUSES, 'DRAFT'),
    revision: asNumber(pick(raw, 'revision')),
    validatedRevision: asNullableNumber(pick(raw, 'validatedRevision', 'validated_revision')),
    resourceTopologyVersionId: asString(pick(
      raw,
      'resourceTopologyVersionId',
      'resource_topology_version_id',
    )),
    resourceTopologyRevision: asNumber(pick(
      raw,
      'resourceTopologyRevision',
      'resource_topology_revision',
    )),
    baseVersionId: asNullableString(pick(raw, 'baseVersionId', 'base_version_id')),
    createdAt: asNullableString(pick(raw, 'createdAt', 'created_at')),
    createdBy: asNullableNumber(pick(raw, 'createdBy', 'created_by')),
    validatedAt: asNullableString(pick(raw, 'validatedAt', 'validated_at')),
    validatedBy: asNullableNumber(pick(raw, 'validatedBy', 'validated_by')),
    publishedAt: asNullableString(pick(raw, 'publishedAt', 'published_at')),
    publishedBy: asNullableNumber(pick(raw, 'publishedBy', 'published_by')),
    updatedAt: asNullableString(pick(raw, 'updatedAt', 'updated_at')),
  };
};

export const mapEquipmentStateTopologyBaseline = (value: unknown): EquipmentStateTopologyBaseline => {
  const raw = asRecord(value);
  return {
    id: asString(pick(raw, 'id', 'versionId', 'version_id')),
    facilityCode: asString(pick(raw, 'facilityCode', 'facility_code'), 'F1'),
    versionNo: asNumber(pick(raw, 'versionNo', 'version_no', 'versionNumber', 'version_number')),
    name: asString(pick(raw, 'name', 'versionName', 'version_name'), '未命名拓扑'),
    status: enumValue(pick(raw, 'status'), VERSION_STATUSES, 'DRAFT'),
    revision: asNumber(pick(raw, 'revision')),
  };
};

export const mapEquipmentStateTopologyResource = (value: unknown): EquipmentStateTopologyResource => {
  const raw = asRecord(value);
  const lifecycle = asString(pick(raw, 'lifecycleStatus', 'lifecycle_status'), 'ACTIVE').toUpperCase();
  return {
    resourceId: asString(pick(raw, 'resourceId', 'resource_id', 'id')),
    code: asString(pick(raw, 'code', 'resourceCode', 'resource_code')),
    name: asString(pick(raw, 'name', 'resourceName', 'resource_name')),
    kind: enumValue(pick(raw, 'kind', 'resourceKind', 'resource_kind'), RESOURCE_KINDS, 'EQUIPMENT'),
    lifecycleStatus: lifecycle === 'RETIRED' ? 'RETIRED' : lifecycle === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
  };
};

export const mapEquipmentStateValue = (value: unknown): EquipmentStateValue => {
  const raw = asRecord(value);
  return {
    code: asString(pick(raw, 'code', 'valueCode', 'value_code')),
    name: asString(pick(raw, 'name', 'valueName', 'value_name')),
    semanticState: (() => {
      const semanticState = asNullableString(pick(raw, 'semanticState', 'semantic_state'));
      return semanticState && SEMANTIC_STATES.includes(semanticState as EquipmentStateSemanticState)
        ? semanticState as EquipmentStateSemanticState
        : null;
    })(),
    isInitial: asBoolean(pick(raw, 'isInitial', 'is_initial')),
    note: asNullableString(pick(raw, 'note')),
  };
};

const mapEquipmentStateTemporalEventReference = (
  value: unknown,
): EquipmentStateTemporalEventReference | null => {
  const raw = asRecord(value);
  const kind = asString(pick(raw, 'kind')).toUpperCase();
  if (kind === 'CLASS_ACTIVATION' || kind === 'RESOURCE_COMMISSIONED_AT') return { kind };
  if (kind === 'STATE_ENTRY') {
    const attributeCode = asString(pick(raw, 'attributeCode', 'attribute_code')).toUpperCase();
    const valueCode = asString(pick(raw, 'valueCode', 'value_code')).toUpperCase();
    return attributeCode && valueCode ? { kind, attributeCode, valueCode } : null;
  }
  if (kind === 'ACTION_EFFECT') {
    const actionCode = asString(pick(raw, 'actionCode', 'action_code')).toUpperCase();
    return actionCode ? { kind, actionCode } : { kind };
  }
  if (kind === 'ACTION_STATUS') {
    const actionCode = asString(pick(raw, 'actionCode', 'action_code')).toUpperCase();
    const status = asString(pick(raw, 'status')).toUpperCase();
    return actionCode && (status === 'COMPLETED' || status === 'RELEASED')
      ? { kind, actionCode, status }
      : null;
  }
  return null;
};

export const mapEquipmentStateTemporalTransition = (
  value: unknown,
): EquipmentStateTemporalTransition | null => {
  const raw = asRecord(value);
  if (Object.keys(raw).length === 0) return null;
  const startRule = asRecord(pick(raw, 'startRule', 'start_rule'));
  const startEvent = mapEquipmentStateTemporalEventReference(pick(startRule, 'event'));
  const refreshRule = asRecord(pick(raw, 'refreshRule', 'refresh_rule'));
  const refreshKind = asString(pick(refreshRule, 'kind')).toUpperCase();
  const expiryRule = asRecord(pick(raw, 'expiryRule', 'expiry_rule'));
  const expression = mapEquipmentTemporalExpression(pick(expiryRule, 'expression'));
  const enforcement = asRecord(pick(raw, 'enforcement'));
  if (!startEvent || !expression
    || asString(pick(startRule, 'kind')).toUpperCase() !== 'EVENT'
    || asString(pick(expiryRule, 'kind')).toUpperCase() !== 'TEMPORAL_EXPRESSION') {
    return null;
  }
  const refresh = refreshKind === 'RESET_ON_EVENT'
    ? (() => {
      const event = mapEquipmentStateTemporalEventReference(pick(refreshRule, 'event'));
      return event ? { kind: 'RESET_ON_EVENT' as const, event } : null;
    })()
    : refreshKind === 'ONE_SHOT'
      ? { kind: 'ONE_SHOT' as const }
      : refreshKind === 'RESET_ON_START_EVENT'
        ? { kind: 'RESET_ON_START_EVENT' as const }
        : null;
  if (!refresh) return null;
  const severity = asString(pick(enforcement, 'severity')).toLowerCase();
  const hardOrSoft = asString(pick(enforcement, 'hardOrSoft', 'hard_or_soft')).toLowerCase();
  if (!['info', 'warning', 'critical'].includes(severity)
    || !['hard', 'soft'].includes(hardOrSoft)) return null;
  return {
    validValueCode: asString(pick(raw, 'validValueCode', 'valid_value_code')).toUpperCase(),
    expiredValueCode: asString(pick(raw, 'expiredValueCode', 'expired_value_code')).toUpperCase(),
    startRule: { kind: 'EVENT', event: startEvent },
    refreshRule: refresh,
    expiryRule: { kind: 'TEMPORAL_EXPRESSION', expression },
    enforcement: {
      constraintCode: asString(pick(enforcement, 'constraintCode', 'constraint_code')).toUpperCase(),
      severity: severity as EquipmentStateTemporalTransition['enforcement']['severity'],
      hardOrSoft: hardOrSoft as EquipmentStateTemporalTransition['enforcement']['hardOrSoft'],
      violationMessageTemplate: asString(pick(
        enforcement,
        'violationMessageTemplate',
        'violation_message_template',
      )),
    },
  };
};

export const mapEquipmentStateTimeDrivenTransition = (
  value: unknown,
): EquipmentStateTimeDrivenTransition | null => {
  const raw = asRecord(value);
  if (Object.keys(raw).length === 0) return null;
  const startRule = asRecord(pick(raw, 'startRule', 'start_rule'));
  const refreshRule = asRecord(pick(raw, 'refreshRule', 'refresh_rule'));
  const expiryRule = asRecord(pick(raw, 'expiryRule', 'expiry_rule'));
  const startKind = asString(pick(startRule, 'kind')).toUpperCase();
  const refreshKind = asString(pick(refreshRule, 'kind')).toUpperCase();
  const expression = mapEquipmentTemporalExpression(pick(expiryRule, 'expression'));
  const sourceValueCode = asString(pick(raw, 'sourceValueCode', 'source_value_code')).toUpperCase();
  const targetValueCode = asString(pick(raw, 'targetValueCode', 'target_value_code')).toUpperCase();
  if (!sourceValueCode || !targetValueCode || !expression
    || asString(pick(expiryRule, 'kind')).toUpperCase() !== 'TEMPORAL_EXPRESSION') return null;

  const actionCode = asString(pick(startRule, 'actionCode', 'action_code')).toUpperCase();
  const normalizedStart = startKind === 'STATE_ENTRY'
    ? { kind: 'STATE_ENTRY' as const }
    : startKind === 'ACTION_BOUNDARY' && actionCode
      ? { kind: 'ACTION_BOUNDARY' as const, actionCode }
      : null;
  const refreshActionCode = asString(pick(refreshRule, 'actionCode', 'action_code')).toUpperCase();
  const normalizedRefresh = refreshKind === 'RESET_ON_START_EVENT'
    ? { kind: 'RESET_ON_START_EVENT' as const }
    : refreshKind === 'ONE_SHOT'
      ? { kind: 'ONE_SHOT' as const }
      : refreshKind === 'RESET_ON_ACTION_BOUNDARY' && refreshActionCode
        ? { kind: 'RESET_ON_ACTION_BOUNDARY' as const, actionCode: refreshActionCode }
        : null;
  return normalizedStart && normalizedRefresh ? {
    sourceValueCode,
    targetValueCode,
    startRule: normalizedStart,
    refreshRule: normalizedRefresh,
    expiryRule: { kind: 'TEMPORAL_EXPRESSION', expression },
  } : null;
};

export const mapEquipmentStateAttribute = (value: unknown): EquipmentStateAttribute => {
  const raw = asRecord(value);
  const values = pick(raw, 'values', 'stateValues', 'state_values');
  return {
    code: asString(pick(raw, 'code', 'attributeCode', 'attribute_code')),
    name: asString(pick(raw, 'name', 'attributeName', 'attribute_name')),
    kind: enumValue(pick(raw, 'kind', 'attributeKind', 'attribute_kind'), ATTRIBUTE_KINDS, 'DISCRETE'),
    unit: asNullableString(pick(raw, 'unit')),
    initialNumber: asNullableNumber(pick(raw, 'initialNumber', 'initial_number')),
    minimum: asNullableNumber(pick(raw, 'minimum')),
    maximum: asNullableNumber(pick(raw, 'maximum')),
    clockKind: (() => {
      const kind = pick(raw, 'clockKind', 'clock_kind');
      return kind == null || kind === '' ? null : enumValue(kind, TIMER_BASES, 'OTHER');
    })(),
    temporalExpression: mapEquipmentTemporalExpression(
      pick(raw, 'temporalExpression', 'temporal_expression'),
      pick(raw, 'durationMinutes', 'duration_minutes'),
    ),
    temporalTransition: mapEquipmentStateTemporalTransition(
      pick(raw, 'temporalTransition', 'temporal_transition'),
    ),
    timeDrivenTransitions: asArray(pick(raw, 'timeDrivenTransitions', 'time_driven_transitions'))
      .map(mapEquipmentStateTimeDrivenTransition)
      .filter((transition): transition is EquipmentStateTimeDrivenTransition => transition !== null),
    note: asNullableString(pick(raw, 'note')),
    values: asArray(values).map(mapEquipmentStateValue),
  };
};

export const mapEquipmentStateCondition = (value: unknown): EquipmentStateCondition => {
  const raw = asRecord(value);
  const rawValue = pick(raw, 'value');
  return {
    attributeCode: asString(pick(raw, 'attributeCode', 'attribute_code')),
    operator: enumValue(pick(raw, 'operator'), OPERATORS, 'EQ'),
    ...(rawValue !== undefined ? { value: rawValue as EquipmentStateCondition['value'] } : {}),
  };
};

export const mapEquipmentStateEffect = (value: unknown): EquipmentStateEffect => {
  const raw = asRecord(value);
  const rawValue = pick(raw, 'value');
  return {
    attributeCode: asString(pick(raw, 'attributeCode', 'attribute_code')),
    effectType: enumValue(pick(raw, 'effectType', 'effect_type'), EFFECT_TYPES, 'SET_STATE'),
    ...(rawValue !== undefined ? { value: rawValue as string | number | null } : {}),
  };
};

export const mapEquipmentStateAction = (value: unknown): EquipmentStateAction => {
  const raw = asRecord(value);
  return {
    actionCode: asString(pick(raw, 'actionCode', 'action_code')),
    name: asString(pick(raw, 'name', 'actionName', 'action_name')),
    operationId: asNullableString(pick(
      raw,
      'operationId',
      'operation_id',
      'sourceOperationId',
      'source_operation_id',
    )),
    effectTiming: enumValue(pick(raw, 'effectTiming', 'effect_timing'), EFFECT_TIMINGS, 'END'),
    origin: enumValue(pick(raw, 'origin'), ACTION_ORIGINS, 'PRIMARY'),
    description: asNullableString(pick(raw, 'description', 'note')),
    guards: asArray(pick(raw, 'guards')).map(mapEquipmentStateCondition),
    effects: asArray(pick(raw, 'effects')).map(mapEquipmentStateEffect),
  };
};

const mapCalendarPattern = (value: unknown): EquipmentCalendarPattern => {
  const raw = asRecord(value);
  const kind = asString(pick(raw, 'kind')).toUpperCase();
  if (kind === 'MONTHLY_DAY') {
    return {
      kind: 'MONTHLY_DAY',
      intervalMonths: asNumber(pick(raw, 'intervalMonths', 'interval_months'), 1),
      dayOfMonth: asNumber(pick(raw, 'dayOfMonth', 'day_of_month'), 1),
      monthEndPolicy: enumValue(pick(raw, 'monthEndPolicy', 'month_end_policy'), MONTH_END_POLICIES, 'CLAMP'),
    };
  }
  if (kind === 'MONTHLY_NTH_WEEKDAY') {
    return {
      kind: 'MONTHLY_NTH_WEEKDAY',
      intervalMonths: asNumber(pick(raw, 'intervalMonths', 'interval_months'), 1),
      ordinal: enumValue(
        pick(raw, 'ordinal'),
        ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'] as const,
        'FIRST',
      ),
      weekday: enumValue(pick(raw, 'weekday'), WEEKDAYS, 'MONDAY'),
    };
  }
  if (kind === 'YEARLY_DATE') {
    return {
      kind: 'YEARLY_DATE',
      intervalYears: asNumber(pick(raw, 'intervalYears', 'interval_years'), 1),
      month: asNumber(pick(raw, 'month'), 1),
      day: asNumber(pick(raw, 'day'), 1),
      monthEndPolicy: enumValue(pick(raw, 'monthEndPolicy', 'month_end_policy'), MONTH_END_POLICIES, 'CLAMP'),
    };
  }
  return {
    kind: 'WEEKLY',
    intervalWeeks: asNumber(pick(raw, 'intervalWeeks', 'interval_weeks'), 1),
    weekdays: asArray(pick(raw, 'weekdays')).map((item) => enumValue(item, WEEKDAYS, 'MONDAY')),
  };
};

const elapsedCycleAmount = (
  value = 1,
  unit: EquipmentCycleElapsedAmount['unit'] = 'DAY',
): EquipmentCycleElapsedAmount => ({
  basis: 'ELAPSED_DURATION', value, unit, monthEndPolicy: null,
});

const calendarCycleAmount = (
  value = 1,
  unit: EquipmentCycleCalendarAmount['unit'] = 'MONTH',
): EquipmentCycleCalendarAmount => ({
  basis: 'CALENDAR_PERIOD',
  value,
  unit,
  monthEndPolicy: ['MONTH', 'QUARTER', 'YEAR'].includes(unit) ? 'CLAMP' : null,
});

const mapCycleTimeExpression = (value: unknown): EquipmentCycleTimeExpression | null => {
  const raw = asRecord(value);
  const basis = asString(pick(raw, 'basis')).toUpperCase();
  if (basis === 'CALENDAR_PERIOD') {
    const unit = enumValue(
      pick(raw, 'unit'),
      ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const,
      'MONTH',
    );
    return {
      basis: 'CALENDAR_PERIOD',
      value: asNumber(pick(raw, 'value'), 1),
      unit,
      monthEndPolicy: ['MONTH', 'QUARTER', 'YEAR'].includes(unit)
        ? enumValue(pick(raw, 'monthEndPolicy', 'month_end_policy'), MONTH_END_POLICIES, 'CLAMP')
        : null,
    };
  }
  if (basis === 'ELAPSED_DURATION') {
    return {
      basis: 'ELAPSED_DURATION',
      value: asNumber(pick(raw, 'value'), 1),
      unit: enumValue(pick(raw, 'unit'), ['SECOND', 'MINUTE', 'HOUR', 'DAY'] as const, 'DAY'),
      monthEndPolicy: null,
    };
  }
  return null;
};

const mapCycleEvent = (value: unknown): EquipmentCycleEventReference => {
  const raw = asRecord(value);
  const kind = asString(pick(raw, 'kind', 'type'), 'CLASS_ACTIVATION').toUpperCase();
  if (kind === 'STATE_ENTRY') {
    return {
      kind: 'STATE_ENTRY',
      attributeCode: asString(pick(raw, 'attributeCode', 'attribute_code')),
      valueCode: asString(pick(raw, 'valueCode', 'value_code')),
    };
  }
  if (kind === 'ACTION_STATUS' || kind === 'ACTION_COMPLETION') {
    return {
      kind: 'ACTION_STATUS',
      actionCode: asString(pick(raw, 'actionCode', 'action_code')),
      status: kind === 'ACTION_COMPLETION'
        ? 'COMPLETED'
        : enumValue(pick(raw, 'status'), ['COMPLETED', 'RELEASED'] as const, 'COMPLETED'),
    };
  }
  if (kind === 'METER_RESET') {
    return { kind: 'METER_RESET', meterCode: asString(pick(raw, 'meterCode', 'meter_code')) };
  }
  if (kind === 'CUSTOM_EVENT') {
    return { kind: 'CUSTOM_EVENT', eventCode: asString(pick(raw, 'eventCode', 'event_code')) };
  }
  if (kind === 'CLASS_ACTIVATION' || kind === 'RESOURCE_COMMISSIONED_AT' || kind === 'CYCLE_SATISFIED') {
    return { kind };
  }
  if (kind === 'CYCLE_COMPLETION') {
    return { kind: 'CYCLE_SATISFIED' };
  }
  return { kind: 'CLASS_ACTIVATION' };
};

const mapBaselineCapture = (value: unknown): EquipmentCycleBaselineCapture => {
  const raw = asRecord(value);
  const source = asRecord(pick(raw, 'source'));
  const sourceKind = asString(pick(source, 'kind')).toUpperCase();
  return {
    key: asString(pick(raw, 'key')),
    source: sourceKind === 'EVENT_FIELD'
      ? { kind: 'EVENT_FIELD', field: asString(pick(source, 'field')) }
      : {
        kind: sourceKind === 'SIGNAL' ? 'SIGNAL' : 'METER',
        code: asString(pick(source, 'code')),
      },
  };
};

const mapCycleStartRule = (value: unknown): EquipmentCycleStartRule => {
  const raw = asRecord(value);
  const captures = asArray(pick(raw, 'baselineCaptures', 'baseline_captures')).map(mapBaselineCapture);
  if (asString(pick(raw, 'kind')).toUpperCase() === 'FIXED_DATE_TIME') {
    return {
      kind: 'FIXED_DATE_TIME',
      fixedDateTime: asString(pick(raw, 'fixedDateTime', 'fixed_date_time')),
      baselineCaptures: captures,
    };
  }
  return {
    kind: 'EVENT',
    event: mapCycleEvent(pick(raw, 'event')),
    retriggerPolicy: enumValue(
      pick(raw, 'retriggerPolicy', 'retrigger_policy'),
      ['IGNORE_WHILE_ACTIVE', 'RESTART', 'REJECT'] as const,
      'IGNORE_WHILE_ACTIVE',
    ),
    baselineCaptures: captures,
  };
};

const mapCycleRefreshRule = (value: unknown) => {
  const raw = asRecord(value);
  const kind = asString(pick(raw, 'kind')).toUpperCase();
  if (kind === 'RESET_ON_EVENT') {
    return {
      kind: 'RESET_ON_EVENT' as const,
      event: mapCycleEvent(pick(raw, 'event')),
      recaptureBaselines: asBoolean(pick(raw, 'recaptureBaselines', 'recapture_baselines')),
    };
  }
  if (kind === 'ROLL_FORWARD_FROM_DUE') {
    return {
      kind: 'ROLL_FORWARD_FROM_DUE' as const,
      missedCyclePolicy: enumValue(
        pick(raw, 'missedCyclePolicy', 'missed_cycle_policy'),
        MISSED_CYCLE_POLICIES,
        'CATCH_UP_LATEST',
      ),
    };
  }
  return { kind: 'ONE_SHOT' as const };
};

export const mapEquipmentCycleRule = (value: unknown): EquipmentCycleExpiryRule => {
  const raw = asRecord(value);
  const kind = asString(pick(raw, 'kind')).toUpperCase();
  if (['ELAPSED_INTERVAL', 'CALENDAR_INTERVAL', 'CALENDAR_PATTERN', 'BUSINESS_INTERVAL',
    'USAGE_THRESHOLD', 'CONDITION_THRESHOLD'].includes(kind)) {
    throw new Error('旧周期策略需复核：旧规则不能自动转换为新的到期判定。');
  }
  if (kind === 'CALENDAR') {
    const expression = mapCycleTimeExpression(pick(raw, 'period', 'interval'));
    return { kind: 'CALENDAR', period: expression?.basis === 'CALENDAR_PERIOD' ? expression : calendarCycleAmount() };
  }
  if (kind === 'ABSOLUTE_DATE_TIME') {
    return { kind: 'ABSOLUTE_DATE_TIME', fixedDateTime: asString(pick(raw, 'fixedDateTime', 'fixed_date_time')) };
  }
  if (kind === 'SCHEDULE') {
    const schedule = asRecord(pick(raw, 'schedule'));
    if (asString(pick(schedule, 'kind')).toUpperCase() === 'BUSINESS_INTERVAL') {
      return {
        kind: 'SCHEDULE',
        schedule: {
          kind: 'BUSINESS_INTERVAL',
          value: asNumber(pick(schedule, 'value'), 1),
          unit: enumValue(pick(schedule, 'unit'), ['BUSINESS_DAY', 'BUSINESS_WEEK'] as const, 'BUSINESS_DAY'),
          businessCalendarCode: asString(pick(schedule, 'businessCalendarCode', 'business_calendar_code')),
        },
      };
    }
    return {
      kind: 'SCHEDULE',
      schedule: {
        kind: 'CALENDAR_PATTERN',
        pattern: mapCalendarPattern(pick(schedule, 'pattern')),
        localTime: asString(pick(schedule, 'localTime', 'local_time'), '00:00:00'),
      },
    };
  }
  if (kind === 'ACCUMULATED') {
    const source = asRecord(pick(raw, 'source'));
    return {
      kind: 'ACCUMULATED',
      source: {
        kind: enumValue(pick(source, 'kind'), ['METER', 'SIGNAL'] as const, 'METER'),
        code: asString(pick(source, 'code')),
      },
      baselineKey: asString(pick(raw, 'baselineKey', 'baseline_key')),
      delta: asNumber(pick(raw, 'delta')),
      unit: asString(pick(raw, 'unit')),
    };
  }
  if (kind === 'THRESHOLD') {
    const source = asRecord(pick(raw, 'source'));
    return {
      kind: 'THRESHOLD',
      source: {
        kind: enumValue(pick(source, 'kind'), ['METER', 'SIGNAL'] as const, 'METER'),
        code: asString(pick(source, 'code')),
      },
      operator: enumValue(pick(raw, 'operator'), ['EQ', 'NE', 'LT', 'LTE', 'GT', 'GTE'] as const, 'GTE'),
      threshold: asNumber(pick(raw, 'threshold')),
      unit: asString(pick(raw, 'unit')),
    };
  }
  if (kind === 'CONDITION') {
    const threshold = pick(raw, 'threshold');
    const sustainFor = mapCycleTimeExpression(pick(raw, 'sustainFor', 'sustain_for'));
    return {
      kind: 'CONDITION',
      signalCode: asString(pick(raw, 'signalCode', 'signal_code')),
      operator: enumValue(pick(raw, 'operator'), ['EQ', 'NE', 'LT', 'LTE', 'GT', 'GTE'] as const, 'GTE'),
      threshold: (typeof threshold === 'string' || typeof threshold === 'number' || typeof threshold === 'boolean')
        ? threshold
        : '',
      unit: asNullableString(pick(raw, 'unit')),
      sustainFor: sustainFor?.basis === 'ELAPSED_DURATION' ? sustainFor : null,
    };
  }
  if (kind === 'EVENT') {
    return { kind: 'EVENT', event: mapCycleEvent(pick(raw, 'event')) };
  }
  if (kind === 'COMPOSITE') {
    return {
      kind: 'COMPOSITE',
      operator: enumValue(pick(raw, 'operator'), ['ANY_OF', 'ALL_OF'] as const, 'ANY_OF'),
      satisfactionMode: enumValue(
        pick(raw, 'satisfactionMode', 'satisfaction_mode'),
        ['LIVE', 'LATCH_UNTIL_REFRESH'] as const,
        'LIVE',
      ),
      rules: asArray(pick(raw, 'rules')).map(mapEquipmentCycleRule),
    };
  }
  if (kind === 'ELAPSED') {
    const expression = mapCycleTimeExpression(pick(raw, 'duration'));
    return { kind: 'ELAPSED', duration: expression?.basis === 'ELAPSED_DURATION' ? expression : elapsedCycleAmount() };
  }
  throw new Error(`无法识别的周期到期判定类型：${kind || '（空）'}。`);
};

export const mapEquipmentCyclePolicy = (value: unknown): EquipmentCyclePolicy => {
  const raw = asRecord(value);
  if (pick(raw, 'startRule', 'start_rule') === undefined
    || pick(raw, 'refreshRule', 'refresh_rule') === undefined
    || pick(raw, 'expiryRule', 'expiry_rule') === undefined) {
    throw new Error('旧周期策略需复核：请按计时启动、周期推进和到期判定三部分重新确认后保存。');
  }
  const window = asRecord(pick(raw, 'window'));
  const enforcement = asRecord(pick(raw, 'enforcement'));
  const severity = asString(pick(enforcement, 'severity'), 'critical').toLowerCase();
  const hardness = asString(pick(enforcement, 'hardOrSoft', 'hard_or_soft'), 'hard').toLowerCase();
  const warning = mapCycleTimeExpression(pick(window, 'warningBefore', 'warning_before'));
  const tolerance = mapCycleTimeExpression(pick(window, 'dueTolerance', 'due_tolerance'));
  const grace = mapCycleTimeExpression(pick(window, 'gracePeriod', 'grace_period'));
  const context = asRecord(pick(raw, 'calculationContext', 'calculation_context'));
  return {
    code: asString(pick(raw, 'code', 'policyCode', 'policy_code')),
    name: asString(pick(raw, 'name', 'policyName', 'policy_name')),
    startRule: mapCycleStartRule(pick(raw, 'startRule', 'start_rule')),
    refreshRule: mapCycleRefreshRule(pick(raw, 'refreshRule', 'refresh_rule')),
    expiryRule: mapEquipmentCycleRule(pick(raw, 'expiryRule', 'expiry_rule')),
    calculationContext: {
      timeZone: asString(
        pick(context, 'timeZone', 'time_zone') ?? pick(raw, 'timeZone', 'time_zone'),
        'Asia/Shanghai',
      ),
      dstGapPolicy: enumValue(
        pick(context, 'dstGapPolicy', 'dst_gap_policy') ?? pick(raw, 'dstGapPolicy', 'dst_gap_policy'),
        DST_GAP_POLICIES,
        'SHIFT_FORWARD',
      ),
      dstOverlapPolicy: enumValue(
        pick(context, 'dstOverlapPolicy', 'dst_overlap_policy') ?? pick(raw, 'dstOverlapPolicy', 'dst_overlap_policy'),
        DST_OVERLAP_POLICIES,
        'EARLIER_OFFSET',
      ),
      adjustmentCalendarCode: asNullableString(
        pick(context, 'adjustmentCalendarCode', 'adjustment_calendar_code')
          ?? pick(raw, 'adjustmentCalendarCode', 'adjustment_calendar_code'),
      ),
      businessDayAdjustment: enumValue(
        pick(context, 'businessDayAdjustment', 'business_day_adjustment')
          ?? pick(raw, 'businessDayAdjustment', 'business_day_adjustment'),
        BUSINESS_ADJUSTMENTS,
        'NONE',
      ),
    },
    window: {
      warningBefore: warning,
      dueTolerance: tolerance,
      gracePeriod: grace,
    },
    enforcement: {
      constraintCode: asString(pick(enforcement, 'constraintCode', 'constraint_code')),
      severity: (['info', 'warning', 'critical'].includes(severity) ? severity : 'critical') as EquipmentStateIssueSeverity,
      hardOrSoft: hardness === 'soft' ? 'soft' : 'hard',
      violationMessageTemplate: asString(
        pick(enforcement, 'violationMessageTemplate', 'violation_message_template'),
      ),
      activationPoint: enumValue(
        pick(enforcement, 'activationPoint', 'activation_point'),
        ['DUE', 'GRACE_END'] as const,
        'DUE',
      ),
    },
    note: asNullableString(pick(raw, 'note')),
  };
};

const mapEquipmentPolicyOverride = (value: unknown): EquipmentPolicyOverride => {
  const raw = asRecord(value);
  const mode = enumValue(pick(raw, 'mode'), OVERRIDE_MODES, 'INHERIT');
  const policyCode = asString(pick(raw, 'policyCode', 'policy_code'));
  return mode === 'REPLACE'
    ? { policyCode, mode, replacement: mapEquipmentCyclePolicy(pick(raw, 'replacement')) }
    : { policyCode, mode, replacement: null };
};

export const mapEquipmentStateClass = (value: unknown): EquipmentStateClass => {
  const raw = asRecord(value);
  const model = asRecord(pick(raw, 'model'));
  const kinds = asArray(pick(raw, 'applicableResourceKinds', 'applicable_resource_kinds'));
  return {
    classId: asString(pick(raw, 'classId', 'class_id', 'id')),
    code: asString(pick(raw, 'code', 'classCode', 'class_code')),
    name: asString(pick(raw, 'name', 'className', 'class_name')),
    fabricationType: enumValue(
      pick(raw, 'fabricationType', 'fabrication_type'),
      FABRICATION_TYPES,
      'OTHER',
    ),
    applicableResourceKinds: kinds.map((kind) => enumValue(kind, RESOURCE_KINDS, 'EQUIPMENT')),
    lifecycleStatus: asString(pick(raw, 'lifecycleStatus', 'lifecycle_status'), 'ACTIVE').toUpperCase() === 'RETIRED'
      ? 'RETIRED'
      : 'ACTIVE',
    note: asNullableString(pick(raw, 'note')),
    model: {
      attributes: asArray(pick(model, 'attributes')).map(mapEquipmentStateAttribute),
      actions: asArray(pick(model, 'actions')).map(mapEquipmentStateAction),
      readyConditions: asArray(pick(model, 'readyConditions', 'ready_conditions')).map(mapEquipmentStateCondition),
      cyclePolicies: asArray(pick(model, 'cyclePolicies', 'cycle_policies')).map(mapEquipmentCyclePolicy),
    },
  };
};

export const mapEquipmentStateBinding = (value: unknown): EquipmentStateBinding => {
  const raw = asRecord(value);
  return {
    resourceId: asString(pick(raw, 'resourceId', 'resource_id')),
    classId: asString(pick(raw, 'classId', 'class_id')),
    policyOverrides: asArray(pick(raw, 'policyOverrides', 'policy_overrides')).map(mapEquipmentPolicyOverride),
  };
};

export const mapEquipmentStateIssue = (value: unknown, index = 0): EquipmentStateValidationIssue => {
  const raw = asRecord(value);
  const constraintCode = asString(pick(raw, 'constraintCode', 'constraint_code'), 'STATE_MODEL_INVALID');
  const severity = asString(pick(raw, 'severity'), 'critical').toLowerCase();
  const hardness = asString(pick(raw, 'hardOrSoft', 'hard_or_soft'), 'hard').toLowerCase();
  const entities = asArray(pick(raw, 'affectedEntities', 'affected_entities'));
  return {
    id: asString(pick(raw, 'id', 'issueId', 'issue_id'), `${constraintCode}-${index}`),
    constraintCode,
    severity: (['info', 'warning', 'critical'].includes(severity) ? severity : 'critical') as EquipmentStateIssueSeverity,
    hardOrSoft: (hardness === 'soft' ? 'soft' : 'hard') as EquipmentStateIssueHardness,
    violationReason: asString(
      pick(raw, 'violationReason', 'violation_reason', 'message'),
      '设备状态机校验未通过',
    ),
    violationMessageTemplate: asNullableString(pick(
      raw,
      'violationMessageTemplate',
      'violation_message_template',
    )),
    affectedEntities: entities.map((entity) => {
      const item = asRecord(entity);
      return {
        entityType: asString(pick(item, 'entityType', 'entity_type'), 'STATE_MODEL'),
        entityId: asString(pick(item, 'entityId', 'entity_id')),
      };
    }).filter((entity) => entity.entityId),
    suggestedAction: asNullableString(pick(raw, 'suggestedAction', 'suggested_action')),
  };
};

export const mapEquipmentStateCatalogWorkspace = (payload: unknown): EquipmentStateCatalogWorkspace => {
  const raw = asRecord(unwrapEquipmentStateCatalogData(payload));
  return {
    version: mapEquipmentStateCatalogVersion(pick(raw, 'version')),
    resourceTopology: mapEquipmentStateTopologyBaseline(pick(raw, 'resourceTopology', 'resource_topology')),
    topologyResources: asArray(pick(raw, 'topologyResources', 'topology_resources'))
      .filter((value) => {
        const resource = asRecord(value);
        const kind = asString(pick(resource, 'kind', 'resourceKind', 'resource_kind')).toUpperCase();
        return RESOURCE_KINDS.includes(kind as EquipmentStateApplicableResourceKind);
      })
      .map(mapEquipmentStateTopologyResource),
    classes: asArray(pick(raw, 'classes', 'equipmentClasses', 'equipment_classes'))
      .map(mapEquipmentStateClass),
    bindings: asArray(pick(raw, 'bindings')).map(mapEquipmentStateBinding),
    issues: asArray(pick(raw, 'issues', 'validationIssues', 'validation_issues'))
      .map(mapEquipmentStateIssue),
  };
};

export const mapEquipmentStateCatalogMutation = (payload: unknown): EquipmentStateCatalogMutationResult => {
  const raw = asRecord(unwrapEquipmentStateCatalogData(payload));
  const rawClass = pick(raw, 'equipmentClass', 'equipment_class', 'class');
  return {
    version: mapEquipmentStateCatalogVersion(pick(raw, 'version')),
    ...(rawClass ? { equipmentClass: mapEquipmentStateClass(rawClass) } : {}),
  };
};

export const mapEquipmentStateCatalogRebase = (payload: unknown): EquipmentStateCatalogRebaseResult => {
  const raw = asRecord(unwrapEquipmentStateCatalogData(payload));
  return {
    version: mapEquipmentStateCatalogVersion(pick(raw, 'version')),
    resourceTopology: mapEquipmentStateTopologyBaseline(pick(raw, 'resourceTopology', 'resource_topology')),
    preservedBindingCount: asNumber(pick(raw, 'preservedBindingCount', 'preserved_binding_count')),
    issues: asArray(pick(raw, 'issues')).map(mapEquipmentStateIssue),
  };
};
